import { get } from 'lodash';
import { z } from 'zod';
import { EmptyValidationMessage, ITFunction, addYears, getNow } from '@ninja/utilities';
import {
	CustomerWebSiteAppInitResponse,
	MedicalDeclarationQuestionsResponse,
	QuestionTypes,
} from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import { Member } from '../../../server';
import { prepareFieldError } from '../members/members-form/helpers/add-field-error';
import calculateAgeInDays from './helpers/calculate-age-in-days';
import { AnswerTypes, GetMemberQuestionsHandler, IDeclarationQuestionsForm } from './types';
import useDeclarationAnswers from './useDeclarationAnswers';

const adultSettings = { minWeight: 2, maxWeight: 300, minHeight: 15, maxHeight: 250 };

/**
 * Get the validation schema type based on the data type id
 * @param dataTypeId - data type id of the question
 * @param t - translation function
 */
const getValidationType = (dataTypeId: number, t: ITFunction) => {
	const invalidDateMessage = t('declaration:card.validation.date.invalid');

	switch (dataTypeId) {
		case QuestionTypes.CheckBox:
			return z.object({
				answer: z.boolean(),
				dataTypeId: z.number(),
			});
		case QuestionTypes.DatePicker:
		case QuestionTypes.YearPicker:
			return z
				.object({
					answer: z.string({ required_error: EmptyValidationMessage }),
					dataTypeId: z.number(),
				})
				.superRefine((value, ctx) => {
					const { answer } = value;
					const addFieldError = prepareFieldError(ctx);
					const date = new Date(answer);

					if (isNaN(date.getTime())) {
						addFieldError({ field: 'answer', message: EmptyValidationMessage });
					}

					const validFromYear = addYears(getNow(), -100);
					const validToYear = addYears(getNow(), 100);

					if (date < validFromYear || date > validToYear) {
						addFieldError({ field: 'answer', message: invalidDateMessage });
					}
				});
		default:
			return z.object({
				answer: z.number({ required_error: EmptyValidationMessage }),
				dataTypeId: z.number(),
			});
	}
};

type Props = {
	getMemberQuestions: GetMemberQuestionsHandler;
	declarationQuestions: MedicalDeclarationQuestionsResponse;
	members: Member[];
	newBornSettings?: NonNullable<CustomerWebSiteAppInitResponse['systemConfigurationSettings']>['newBornSettings'];
};

const useDeclarationValidation = (props: Props) => {
	const { getMemberQuestions, declarationQuestions, members, newBornSettings } = props;
	const { translate: t } = useAppPlatformContextOptions();
	const { getAnswerIdFromSavedAnswer } = useDeclarationAnswers({ declarationQuestions });

	const getSubQuestions = (
		memberId: string,
		formValues: IDeclarationQuestionsForm,
		questionsIds: number[] | undefined
	): { questionId: number; answerId?: AnswerTypes; dataTypeId?: number }[] | undefined => {
		return questionsIds?.flatMap((questionId) => {
			const answerId = get(formValues, [memberId, 'questions', `q-${questionId}`])?.answer;
			//DataTypeId is required to get the validation schema
			const dataTypeId = get(formValues, [memberId, 'questions', `q-${questionId}`])?.dataTypeId;

			const answerValue = answerId === true ? getAnswerIdFromSavedAnswer?.(memberId, questionId, answerId) : answerId;

			const subQuestions = getMemberQuestions(memberId, questionId);

			const subQuestionsIds = subQuestions
				?.filter((q) => q.answers.some((a) => a.answerParentId === answerValue))
				.map((q) => q.id);

			return [{ questionId, answerId, dataTypeId }, ...(getSubQuestions(memberId, formValues, subQuestionsIds) ?? [])];
		});
	};

	const getMemberQuestionsAnswers = (memberId: string, formValues: IDeclarationQuestionsForm) => {
		const parentQuestionsIds = getMemberQuestions(memberId, null)?.map((q) => q.id);
		const memberQuestionsIds = getSubQuestions(memberId, formValues, parentQuestionsIds);

		return memberQuestionsIds;
	};

	const getQuestionsValidationSchema = (memberId: string, formValues: IDeclarationQuestionsForm) => {
		const memberQuestionsAnswers = getMemberQuestionsAnswers(memberId, formValues);

		const questions = memberQuestionsAnswers?.reduce?.((acc, questionWithAnswer) => {
			return {
				...acc,
				[`q-${questionWithAnswer.questionId}`]: getValidationType(
					questionWithAnswer.dataTypeId ?? QuestionTypes.BooleanQuestion,
					t
				),
			};
		}, {});

		return questions;
	};

	const getValidationSchema = (formValues: IDeclarationQuestionsForm) => {
		const weightInvalidMessage = t('declaration:card.validation.weight.invalid');
		const heightInvalidMessage = t('declaration:card.validation.height.invalid');

		const membersIds = declarationQuestions?.map((member) => member.memberId);
		const validationSchema = z.object(
			membersIds.reduce((acc, memberId) => {
				const questionValidationSchema = getQuestionsValidationSchema(memberId, formValues);

				return {
					...acc,
					[memberId]: z
						.object({
							isMedicalConditionDeclared: z.number({
								required_error: EmptyValidationMessage,
							}),
							weight: z.number({ required_error: EmptyValidationMessage }),
							height: z.number({ required_error: EmptyValidationMessage }),
							questions:
								formValues[memberId]?.isMedicalConditionDeclared === 0 || !questionValidationSchema
									? z.any()
									: z.object(questionValidationSchema),
						})
						.superRefine((values, ctx) => {
							const addFieldError = prepareFieldError(ctx);

							const { height, weight } = values;
							const mDob = members.find((m) => m.memberId === memberId)?.dateOfBirth;
							const inDays = calculateAgeInDays(mDob!);

							if (newBornSettings && inDays <= newBornSettings?.maxNumberOfDays) {
								if (weight < newBornSettings?.minWeight || weight > newBornSettings?.maxWeight)
									addFieldError({ field: 'weight', message: weightInvalidMessage });

								if (height < newBornSettings?.minHeight || height > newBornSettings?.maxHeight)
									addFieldError({ field: 'height', message: heightInvalidMessage });
							} else {
								if (weight < adultSettings.minWeight || weight > adultSettings.maxWeight)
									addFieldError({ field: 'weight', message: weightInvalidMessage });

								if (height < adultSettings.minHeight || height > adultSettings.maxHeight)
									addFieldError({ field: 'height', message: heightInvalidMessage });
							}
						}),
				};
			}, {})
		);

		return validationSchema;
	};

	return { getValidationSchema };
};

export default useDeclarationValidation;
